import React, {FC, useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useLinkedSettingsArrowOffset} from 'src/hooks/useLinkedSettingsArrowOffset'
import {
  FIELD_MAPPING_DATA,
  FIELD_MAPPING_TYPE,
  PlatformDataType,
  RetrievePlatformDataType,
  SECTION_WISE_SAVED_FIELD_MAPPING,
  SETTINGS_OF_PLATFORM,
} from 'src/interfaces/Platform.interface'
import CustomInput from '../inputs/CustomInput'
import {PlatformSettingDataTypesValues} from 'src/app/_interfaces/setting.interface'
import ShortCodeInput from '../inputs/ShortCodeInput'
import MappingInput from '../mapping/MappingInput'
import DependentMapping from '../mapping/DependentMapping'
import SettingFileActionCallout from 'src/app/pages/listing/components/SettingFileActionCallout'
import {
  isSettingFileActionMapping,
  isSettingFileChildInputType,
  partitionFieldMappings,
  partitionSettingChildren,
} from 'src/utils/settingFileFieldMappings'
import {
  handleBooleanSettingChange,
  isTruthySettingValue,
  resolveSettingFieldPath,
  SettingBooleanFieldSetter,
} from 'src/utils/normalizeBooleanSettingValue'
import {shouldRenderChildByDropdownControl} from 'src/utils/settingControlVisibility'
import {
  getSequenceCustomInputContainerClass,
  getSequenceFieldShellStyle,
  getSequenceFieldWrapperClass,
  getSequenceInnerColumnClass,
} from 'src/utils/channelSettingSequenceLayout'
import {applyPlatformSettingShortcodes} from 'src/utils/platformSettingDisplayText'
import SequenceFieldShell from 'src/app/pages/listing/components/SequenceFieldShell'
import {
  ChannelSettingLinkedPanelLayoutProvider,
  useChannelSettingLinkedPanelLayout,
} from 'src/app/pages/listing/components/ChannelSettingLinkedPanelLayoutContext'
import {useParams} from 'react-router-dom'
import InfoTooltip from '../common/InfoTooltip'
import moment from 'moment'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import Select from 'react-select'
import {getPlatformFieldsAsync} from 'src/redux/actions/productActions'
import {getPlatformParentFieldDataCategoriesAsync} from 'src/redux/actions/templateAndPricingActions'
import {
  buildPayoutCostsMappingMeta,
  buildPayoutDependentDestinationMappings,
  buildPayoutFieldMappingDedupeKey,
  findDependentSavedFieldMapping,
  findSavedPayoutCostsSourceField,
  mergePayoutPlatformFieldSide,
  resolvePayoutCostsMapping,
  resolvePayoutMappingPlatformSides,
  isPayoutRelatedFieldCode,
  serializeChildFieldMappingsKey,
  serializeOrderMappingsKey,
  serializeSavedPayoutMappingsKey,
} from 'src/utils/payoutMapping'

type ChildFieldMappingMeta = FIELD_MAPPING_TYPE & {id?: number}

const GROUP2_SETTING_GROUP_CODES = [
  'ORDER',
  'ACCOUNTING_ORDER',
  'ERP_ORDER',
  'OMS_ORDER',
  'BACK_SHIPMENT',
] as const

const isGroup2SettingGroupCode = (groupCode?: string | null) =>
  GROUP2_SETTING_GROUP_CODES.includes(
    `${groupCode ?? ''}` as (typeof GROUP2_SETTING_GROUP_CODES)[number]
  )

const getSettingMappingId = (mapping: ChildFieldMappingMeta): number | undefined =>
  mapping.setting_mapping_id ?? mapping.id

const extractPlatformFieldDataSet = (payload: any): any[] => {
  if (Array.isArray(payload)) {
    return payload
  }

  if (Array.isArray(payload?.data)) {
    return payload.data
  }

  if (Array.isArray(payload?.results)) {
    return payload.results
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data
  }

  return []
}

const dedupeChildFieldMappingsByCode = (fieldMappings: ChildFieldMappingMeta[] = []) => {
  const deduped = new Map<string, ChildFieldMappingMeta>()
  fieldMappings.forEach((mapping) => {
    const key = buildPayoutFieldMappingDedupeKey(mapping)
    if (!key || deduped.has(key)) return
    deduped.set(key, mapping)
  })
  return Array.from(deduped.values())
}

const savedMappingRowHasValue = (row: any): boolean => {
  if (!row) return false
  if (row?.destination_platform_data?.id != null && `${row.destination_platform_data.id}` !== '') {
    return true
  }
  if (row?.destination_platform_data_id != null && `${row.destination_platform_data_id}` !== '') {
    return true
  }
  if (
    row?.destination_platform_data_str != null &&
    `${row.destination_platform_data_str}`.trim() !== ''
  ) {
    return true
  }
  if (row?.source_platform_data?.id != null && `${row.source_platform_data.id}` !== '') {
    return true
  }
  if (row?.source_platform_data_id != null && `${row.source_platform_data_id}` !== '') {
    return true
  }
  if (row?.source_platform_data_str != null && `${row.source_platform_data_str}`.trim() !== '') {
    return true
  }
  return false
}

const findDefaultChildSavedMapping = (
  fieldMapping: ChildFieldMappingMeta,
  allSavedMappings: SECTION_WISE_SAVED_FIELD_MAPPING[],
  settingId: number | null
) => {
  if (!allSavedMappings?.length) return null

  const settingMappingId = getSettingMappingId(fieldMapping)
  if (settingMappingId) {
    const bySettingMappingId = allSavedMappings.find(
      (item) => item?.setting_mapping_id === settingMappingId
    )
    if (bySettingMappingId) return bySettingMappingId
  }

  const matches = allSavedMappings.filter((item) => {
    if (item?.destination_field?.api_code !== fieldMapping.field_code) return false
    if (item?.source_field != null) return false
    if (settingId && item?.setting_id && item.setting_id !== settingId) return false
    return true
  })

  if (!matches.length) return null

  // Prefer a duplicate row that still has a selected value (Farfetch PACKAGE_NAME
  // can accumulate empty sibling rows that would otherwise win on .find()).
  return (
    matches.find((item) =>
      (item?.fieldmappingdata_set ?? []).some((row: any) => savedMappingRowHasValue(row))
    ) ?? matches[0]
  )
}

const DynamicChildSetting: FC<{
  destinationPlatform?: any
  childItem: SETTINGS_OF_PLATFORM
  childItemIndex: number
  settingField: string
  values: PlatformSettingDataTypesValues
  handleChange: (e: React.ChangeEvent<any>) => void
  setFieldValue?: SettingBooleanFieldSetter
  savedMappings: SECTION_WISE_SAVED_FIELD_MAPPING[] | []
  settingName: string
  settingId: number | null
  setFieldMappings: React.Dispatch<React.SetStateAction<FIELD_MAPPING_DATA[]>>
  fieldMappings: FIELD_MAPPING_DATA[]
  mainStoreData?: PlatformDataType | null
  retrivedPlatformData?: RetrievePlatformDataType | null
  mappingSourceSide?: number | null
  mappingDestinationSide?: number | null
  mappingSourcePlatform?: string
  mappingDestinationPlatform?: string
  usesSourceChildMappings?: boolean
  groupCode?: string
  secondaryOrderMappingsData?: any[]
  sourceStoreData?: PlatformDataType | null
  setSavedMappings?: React.Dispatch<React.SetStateAction<any[]>>
  platformCode?: string
  oppositeChildSiblings?: SETTINGS_OF_PLATFORM[]
  settingControlRenderOptions?: import('src/utils/settingControlVisibility').ChildSettingControlRenderOptions
  sequencePresentationPart?: 'toggle' | 'panel'
  sequenceLinkedParentToggleRef?: React.RefObject<HTMLDivElement>
  sequenceLinkedChildPanelRef?: React.RefObject<HTMLDivElement>
  sourceChannelName?: string | null
}> = ({
  destinationPlatform,
  childItem,
  childItemIndex,
  settingField,
  values,
  handleChange,
  setFieldValue,
  savedMappings,
  settingId,
  settingName,
  setFieldMappings,
  fieldMappings,
  retrivedPlatformData,
  mappingSourceSide,
  mappingDestinationSide,
  mappingSourcePlatform,
  mappingDestinationPlatform,
  usesSourceChildMappings = false,
  groupCode,
  secondaryOrderMappingsData = [],
  sourceStoreData,
  setSavedMappings,
  platformCode,
  oppositeChildSiblings = [],
  settingControlRenderOptions,
  sequencePresentationPart,
  sequenceLinkedParentToggleRef,
  sequenceLinkedChildPanelRef,
  sourceChannelName,
}) => {
  const id = useId()
  const insideLinkedPanel = useChannelSettingLinkedPanelLayout()
  const sequenceFieldWrapperClass = (legacyClass = 'col-12 col-sm-6 mt-4') =>
    getSequenceFieldWrapperClass(legacyClass, {insideLinkedPanel})
  const dispatch = useAppDispatch()
  const {selectedPlatformId} = useParams()
  const {mainStoreData} = useAppSelector((state) => state.fetchConnectedPlatform)
  const [syncStartDate, setSyncStartDate] = useState(false)
  const [dependentDestinationFields, setDependentDestinationFields] = useState<
    Record<string, any>
  >({})
  const [dependentSourceFields, setDependentSourceFields] = useState<Record<string, any>>({})
  const localLinkedParentToggleRef = useRef<HTMLDivElement>(null)
  const localLinkedChildPanelRef = useRef<HTMLDivElement>(null)
  const linkedParentToggleRef =
    sequenceLinkedParentToggleRef ?? localLinkedParentToggleRef
  const linkedChildPanelRef =
    sequenceLinkedChildPanelRef ?? localLinkedChildPanelRef
  const nestedPanelWasVisibleRef = useRef(false)
  let nestedPanelVisibleThisRender = false
  useLayoutEffect(() => {
    if (nestedPanelVisibleThisRender) {
      nestedPanelWasVisibleRef.current = true
    }
  })
  const isBooleanChildEnabled =
    childItem.input_type === 'BOOLEAN' &&
    Boolean(values?.[`${settingField}_setting`]?.[`${childItem.name}`])
  useLinkedSettingsArrowOffset(
    isBooleanChildEnabled,
    linkedParentToggleRef,
    linkedChildPanelRef
  )

  const sourcePlatformName =
    mainStoreData?.platform_info?.display_name ||
    mainStoreData?.display_name ||
    retrivedPlatformData?.platform_settings?.shipment_setting?.selected_source_platform
      ?.display_name ||
    ''

  const destinationPlatformName =
    mappingDestinationPlatform ||
    destinationPlatform ||
    retrivedPlatformData?.platform_info?.display_name ||
    retrivedPlatformData?.display_name ||
    ''
  const sourcePlatformDisplayName = mappingSourcePlatform || sourcePlatformName
  const formatSettingDisplayText = (text: string | null | undefined) =>
    applyPlatformSettingShortcodes(text, {
      retrievedPlatform: retrivedPlatformData,
      mainStore: mainStoreData,
      sourceChannelName,
    })
  const sourceSideId = mappingSourceSide ?? mainStoreData?.id
  const destinationSideId = mappingDestinationSide ?? Number(selectedPlatformId)
  const childFieldMappingsKey = useMemo(
    () => serializeChildFieldMappingsKey(childItem.field_mappings ?? []),
    [childItem.field_mappings]
  )
  const orderMappingsKey = useMemo(
    () => serializeOrderMappingsKey(secondaryOrderMappingsData),
    [secondaryOrderMappingsData]
  )
  const savedPayoutMappingsKey = useMemo(
    () => serializeSavedPayoutMappingsKey(savedMappings, settingId),
    [savedMappings, settingId]
  )
  const normalizedFieldMappings = useMemo(
    () => dedupeChildFieldMappingsByCode(childItem.field_mappings ?? []),
    [childFieldMappingsKey, childItem.field_mappings]
  )
  const defaultFieldMappings = useMemo(
    () => normalizedFieldMappings.filter((mapping) => mapping.isDefault !== false),
    [normalizedFieldMappings]
  )
  const nonDefaultFieldMappings = useMemo(
    () => normalizedFieldMappings.filter((mapping) => mapping.isDefault === false),
    [normalizedFieldMappings]
  )
  const isOrderPayoutChild = childItem.name === 'isImportOrderPayout'
  const isPayoutSyncEnabled = isOrderPayoutChild
    ? Boolean(values?.[`${settingField}_setting`]?.[childItem.name])
    : true
  const useGroup2ChildMappingDirection = isGroup2SettingGroupCode(groupCode)
  const payoutCostsMapping = useMemo(
    () =>
      resolvePayoutCostsMapping({
        orderMappings: secondaryOrderMappingsData,
        savedMappings,
        payoutFieldMeta: childItem.field_mappings?.find(
          (mapping) => mapping.field_code === 'PAYOUT_COSTS'
        ),
        settingId,
      }),
    [orderMappingsKey, savedPayoutMappingsKey, childFieldMappingsKey, settingId, secondaryOrderMappingsData, savedMappings, childItem.field_mappings]
  )
  const effectivePayoutCostsMapping = useMemo(() => {
    if (payoutCostsMapping) return payoutCostsMapping

    const savedSource = findSavedPayoutCostsSourceField(savedMappings, settingId)
    if (savedSource) return savedSource

    return buildPayoutCostsMappingMeta(
      childItem.field_mappings?.find((mapping) => mapping.field_code === 'PAYOUT_COSTS')
    )
  }, [payoutCostsMapping, savedPayoutMappingsKey, settingId, savedMappings, childFieldMappingsKey, childItem.field_mappings])


  useEffect(() => {
    if (!isOrderPayoutChild || isPayoutSyncEnabled) {
      return
    }

    setFieldMappings((prev) => {
      const next = prev.filter(
        (mapping) => !isPayoutRelatedFieldCode(mapping?.fieldCode)
      )
      return next.length === prev.length ? prev : next
    })
  }, [isOrderPayoutChild, isPayoutSyncEnabled, setFieldMappings])


  const payoutCostsApiCode = effectivePayoutCostsMapping?.api_code ?? 'PAYOUT_COSTS'
  const payoutDependentDestinationMappings = useMemo(
    () =>
      buildPayoutDependentDestinationMappings({
        nonDefaultFieldMappings,
        savedMappings,
        settingId,
      }),
    [nonDefaultFieldMappings, savedPayoutMappingsKey, settingId, savedMappings]
  )
  const dependentFieldCodesKey = useMemo(
    () => {
      if (isOrderPayoutChild) {
        return [
          ...defaultFieldMappings.map((mapping) => mapping.field_code).filter(Boolean),
          ...nonDefaultFieldMappings
            .filter((mapping) => mapping.map_with)
            .flatMap((mapping) => [
              mapping.field_code ?? mapping.api_code,
              mapping.map_with?.field_code ?? mapping.map_with?.api_code,
            ]),
        ]
          .filter(Boolean)
          .sort()
          .join(',')
      }

      return payoutDependentDestinationMappings
        .map((mapping) => mapping.field_code)
        .filter(Boolean)
        .sort()
        .join(',')
    },
    [
      isOrderPayoutChild,
      defaultFieldMappings,
      nonDefaultFieldMappings,
      payoutDependentDestinationMappings,
    ]
  )
  const isErpSelectedView = Boolean(
    retrivedPlatformData?.isErpStore ||
      `${retrivedPlatformData?.platform_info?.nature ?? ''}`.toUpperCase().includes('ERP')
  )
  const payoutPlatformSides = useMemo(
    () =>
      resolvePayoutMappingPlatformSides({
        savedMappings,
        payoutCostsMapping: effectivePayoutCostsMapping,
        selectedPlatformId,
        sourceStoreData,
        mainStoreData,
        isErpSelectedView,
        settingId,
      }),
    [
      savedMappings,
      effectivePayoutCostsMapping,
      selectedPlatformId,
      sourceStoreData,
      mainStoreData,
      isErpSelectedView,
      settingId,
    ]
  )
  const payoutSourceSideId = usesSourceChildMappings
    ? payoutPlatformSides.sourcePlatformId ?? Number(selectedPlatformId) ?? sourceSideId
    : sourceSideId
  const payoutDestinationSideId = usesSourceChildMappings
    ? payoutPlatformSides.destinationPlatformId ?? sourceStoreData?.id ?? mainStoreData?.id
    : destinationSideId
  const isOrderPayoutChildForDefaults = isOrderPayoutChild
  const erpDefaultDestinationSideId =
    payoutPlatformSides.destinationPlatformId ?? sourceStoreData?.id ?? destinationSideId
  const erpDefaultDestinationPlatformName = isErpSelectedView
    ? retrivedPlatformData?.platform_info?.display_name ?? retrivedPlatformData?.display_name
    : sourceStoreData?.platform_info?.display_name ?? sourceStoreData?.display_name
  const defaultMappingDestinationSideId = isOrderPayoutChildForDefaults
    ? erpDefaultDestinationSideId
    : destinationSideId
  const defaultMappingDestinationPlatformName = isOrderPayoutChildForDefaults
    ? erpDefaultDestinationPlatformName
    : destinationPlatformName
  const dependentFieldsFetchKey = `${payoutSourceSideId}|${payoutDestinationSideId}|${dependentFieldCodesKey}|${settingId}|${payoutCostsApiCode}|${savedPayoutMappingsKey}`
  const dependentDestinationFieldsRef = useRef(dependentDestinationFields)
  const dependentSourceFieldsRef = useRef(dependentSourceFields)
  dependentDestinationFieldsRef.current = dependentDestinationFields
  dependentSourceFieldsRef.current = dependentSourceFields

  useEffect(() => {
    if (sequencePresentationPart === 'toggle') {
      return
    }

    const shouldFetchPayoutFields =
      (usesSourceChildMappings || isOrderPayoutChild) &&
      payoutSourceSideId &&
      payoutDestinationSideId &&
      dependentFieldCodesKey

    if (!shouldFetchPayoutFields) {
      return
    }

    const fieldCodes = dependentFieldCodesKey.split(',').filter(Boolean)
    const sourceNeedsOptions = !dependentSourceFieldsRef.current[payoutCostsApiCode]?.platformfielddata_set
      ?.length
    const destinationNeedsOptions = fieldCodes.some((fieldCode) => {
      if (fieldCode === payoutCostsApiCode) return false
      return !dependentDestinationFieldsRef.current[fieldCode]?.platformfielddata_set?.length
    })

    if (!sourceNeedsOptions && !destinationNeedsOptions) {
      return
    }

    let isCancelled = false

    const fetchDependentMappingFields = async () => {
      const fetchedDestinationFields: Record<string, any> = {}
      const fetchedSourceFields: Record<string, any> = {}

      if (sourceNeedsOptions) {
        const sourceResponse: any = await dispatch(
          getPlatformFieldsAsync({
            platformId: Number(payoutSourceSideId),
            otherParams: {field_code: payoutCostsApiCode},
          })
        )
        let sourceField = sourceResponse?.payload?.[0]

        if (!sourceField?.id) {
          sourceField =
            findSavedPayoutCostsSourceField(savedMappings, settingId) ??
            (effectivePayoutCostsMapping?.id ? effectivePayoutCostsMapping : null)
        }

        if (sourceField?.id) {
          const sourceDataResponse: any = await dispatch(
            getPlatformParentFieldDataCategoriesAsync({platform_Id: sourceField.id})
          )
          fetchedSourceFields[payoutCostsApiCode] = {
            ...sourceField,
            platformfielddata_set: extractPlatformFieldDataSet(sourceDataResponse?.payload),
          }
        }
      }

      await Promise.all(
        fieldCodes.map(async (fieldCode) => {
          if (fieldCode === payoutCostsApiCode) {
            return
          }

          if (dependentDestinationFieldsRef.current[fieldCode]?.platformfielddata_set?.length) {
            return
          }

          const response: any = await dispatch(
            getPlatformFieldsAsync({
              platformId: Number(payoutDestinationSideId),
              otherParams: {field_code: fieldCode},
            })
          )

          const destinationField = response?.payload?.[0]
          if (!destinationField?.id) return

          const parentDataResponse: any = await dispatch(
            getPlatformParentFieldDataCategoriesAsync({platform_Id: destinationField.id})
          )

          fetchedDestinationFields[fieldCode] = {
            ...destinationField,
            platformfielddata_set: extractPlatformFieldDataSet(parentDataResponse?.payload),
          }
        })
      )

      if (!isCancelled) {
        if (Object.keys(fetchedSourceFields).length) {
          setDependentSourceFields((prev) => ({...prev, ...fetchedSourceFields}))
        }
        if (Object.keys(fetchedDestinationFields).length) {
          setDependentDestinationFields((prev) => ({...prev, ...fetchedDestinationFields}))
        }
      }
    }

    fetchDependentMappingFields()

    return () => {
      isCancelled = true
    }
  }, [
    sequencePresentationPart,
    dependentFieldsFetchKey,
    dispatch,
    usesSourceChildMappings,
    isOrderPayoutChild,
    payoutCostsApiCode,
    dependentFieldCodesKey,
    payoutSourceSideId,
    payoutDestinationSideId,
  ])

  function formatDateString(dateString: string) {
    // Parse the date string to a Date object
    let date
    if (dateString) {
      if (dateString.endsWith('Z')) {
        let zremovedval = dateString.slice(0, dateString.length - 4)
        console.log('zremovedval', zremovedval)
        // If the date string ends with 'Z', it's in UTC
        date = new Date(zremovedval)
      } else {
        // Otherwise, it's in the local time zone
        date = new Date(dateString)
      }
      // Adjust the date to the local time zone if needed
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)

      // Format the local date for the datetime-local input
      if (isNaN(localDate.getTime())) {
        return ''
      }
      return localDate.toISOString().slice(0, 16)
    } else {
      return null
    }
  }

  if (childItem.input_type === 'BOOLEAN') {
    const isEnabled = isOrderPayoutChild
      ? isPayoutSyncEnabled
      : isTruthySettingValue(values?.[`${settingField}_setting`]?.[`${childItem.name}`])
    const hasNestedContent =
      Boolean(childItem?.children?.length) || Boolean(childItem?.field_mappings?.length)
    const {fileActionChildren, standardChildren} = partitionSettingChildren(
      (childItem.children ?? []).filter((childSetting) =>
        shouldRenderChildByDropdownControl(
          childSetting,
          childItem.children ?? [],
          values,
          settingField,
          oppositeChildSiblings,
          settingControlRenderOptions
        )
      )
    )
    const {fileActionMappings} = partitionFieldMappings(childItem.field_mappings ?? [])
    const hasFileActions = fileActionChildren.length > 0 || fileActionMappings.length > 0
    const standardDefaultFieldMappings = defaultFieldMappings.filter(
      (mapping) => !isSettingFileActionMapping(mapping)
    )
    const hasNonDefaultMappings =
      !usesSourceChildMappings && nonDefaultFieldMappings.length > 0
    const hasPayoutDependentMappings =
      usesSourceChildMappings &&
      Boolean(effectivePayoutCostsMapping) &&
      payoutDependentDestinationMappings.length > 0
    const hasPanelContent =
      hasFileActions ||
      standardChildren.length > 0 ||
      standardDefaultFieldMappings.length > 0 ||
      hasNonDefaultMappings ||
      hasPayoutDependentMappings
    const showNestedPanel = isEnabled && hasNestedContent && hasPanelContent
    nestedPanelVisibleThisRender = showNestedPanel

    const renderNonDefaultFieldMappings = () =>
      nonDefaultFieldMappings.map((field_mapping, mappingIndex) => {
        const mapWith = field_mapping.map_with
        if (mapWith) {
          const sourceApiCode = field_mapping.api_code ?? field_mapping.field_code
          const destApiCode = mapWith.api_code ?? mapWith.field_code
          const effectiveSettingMappingId =
            getSettingMappingId(field_mapping as ChildFieldMappingMeta) ??
            field_mapping.setting_mapping_id ??
            mapWith.setting_mapping_id ??
            null
          const savedMapping = findDependentSavedFieldMapping(
            sourceApiCode,
            destApiCode,
            savedMappings,
            settingId,
            effectiveSettingMappingId
          )

          const resolvedDestinationField = isOrderPayoutChild
            ? mergePayoutPlatformFieldSide({
                fetchedField: dependentDestinationFields[destApiCode],
                savedField: savedMapping?.destination_field,
                meta: mapWith,
                platformId: payoutDestinationSideId,
              })
            : null
          const resolvedSourceField = isOrderPayoutChild
            ? mergePayoutPlatformFieldSide({
                fetchedField: dependentSourceFields[payoutCostsApiCode],
                savedField: savedMapping?.source_field ?? field_mapping,
                meta: field_mapping,
                platformId: payoutSourceSideId,
              })
            : null

          const enrichedDestinationMapping = resolvedDestinationField
            ? {
                ...resolvedDestinationField,
                api_code: destApiCode,
                field_code: destApiCode,
                display_name: mapWith.display_name || resolvedDestinationField.display_name,
                platform: resolvedDestinationField.platform ?? payoutDestinationSideId,
                isDefault: false,
                isRequired: mapWith.isRequired,
                setting_mapping_id: effectiveSettingMappingId,
              }
            : mapWith
          const enrichedSourceMapping = resolvedSourceField
            ? {
                ...resolvedSourceField,
                ...field_mapping,
                api_code: sourceApiCode,
                field_code: sourceApiCode,
                platform: resolvedSourceField.platform ?? payoutSourceSideId,
                isDefault: false,
                isRequired: field_mapping.isRequired,
                setting_mapping_id: effectiveSettingMappingId,
                map_with: enrichedDestinationMapping,
              }
            : field_mapping

          return (
            <div
              key={`dependent_${effectiveSettingMappingId ?? field_mapping.field_code}_${destApiCode}_${mappingIndex}`}
              className='col-12'
            >
              <DependentMapping
                selectedPlatformId={Number(
                  isOrderPayoutChild ? payoutSourceSideId ?? selectedPlatformId : selectedPlatformId
                )}
                sourcePlatform={
                  isOrderPayoutChild
                    ? retrivedPlatformData?.platform_info?.display_name ??
                      retrivedPlatformData?.display_name
                    : sourceStoreData?.platform_info?.display_name ??
                      sourceStoreData?.display_name ??
                      sourcePlatformDisplayName
                }
                destinationPlatform={
                  isOrderPayoutChild
                    ? sourceStoreData?.platform_info?.display_name ??
                      sourceStoreData?.display_name ??
                      defaultMappingDestinationPlatformName
                    : retrivedPlatformData?.platform_info?.display_name ??
                      retrivedPlatformData?.display_name ??
                      destinationPlatformName
                }
                isMappingVisible={isEnabled}
                handleChange={handleChange}
                field_mapping={enrichedSourceMapping}
                isMappedWith={enrichedDestinationMapping}
                savedMapping={savedMapping}
                settingName={settingName}
                settingId={settingId}
                setFieldMappings={setFieldMappings}
                fieldMappings={fieldMappings}
                index={mappingIndex}
                savedMappings={savedMappings}
                setSavedMappings={setSavedMappings!}
                primary_settings={true}
                group_code={groupCode ?? 'ERP_ORDER'}
                platformCode={(platformCode ?? '').toUpperCase()}
                useGroup2ChildMappingDirection={useGroup2ChildMappingDirection}
              />
            </div>
          )
        }

        return (
          <div
            key={`${getSettingMappingId(field_mapping) ?? field_mapping.field_code}_${field_mapping.field_code}`}
            className='col-12 col-lg-6'
          >
            <MappingInput
              destinationPlatform={destinationPlatformName}
              sourcePlatform={sourcePlatformDisplayName}
              selectedPlatformId={Number(selectedPlatformId)}
              fullWidth
              wrapperClassName='mb-0'
              isMappingVisible={isEnabled}
              handleChange={handleChange}
              field_mapping={field_mapping}
              sourceSide={sourceSideId!}
              destinationSide={destinationSideId}
              savedMapping={
                savedMappings?.length
                  ? savedMappings.find(
                      (it) => it?.destination_field?.api_code === field_mapping.field_code
                    ) ?? null
                  : null
              }
              settingName={settingName}
              settingId={settingId}
              setFieldMappings={setFieldMappings}
              fieldMappings={fieldMappings}
            />
          </div>
        )
      })

    const booleanInputId = childItem.name + childItemIndex
    const booleanLabel = (
      <label className='form-label'>
        {formatSettingDisplayText(childItem.display_name)} &nbsp;&nbsp;
        <InfoTooltip message={childItem.description}
        sourceChannelName={sourceChannelName} />
      </label>
    )
    const booleanSwitch = (
      <div className='form-check form-switch form-check-solid mb-0'>
        <input
          type='checkbox'
          className='form-check-input h-30px w-50px'
          name={settingField ? `${settingField}_setting.${childItem.name}` : childItem.name}
          placeholder={formatSettingDisplayText(childItem?.placeholder ?? '')}
          checked={isTruthySettingValue(
            settingField
              ? values[`${settingField}_setting`]?.[`${childItem.name}`]
              : values[childItem.name]
          )}
          onChange={
            setFieldValue
              ? handleBooleanSettingChange(
                  setFieldValue,
                  resolveSettingFieldPath(settingField, childItem.name)
                )
              : handleChange
          }
          id={booleanInputId}
        />
      </div>
    )

    return (
      <>
        {sequencePresentationPart !== 'panel' ? (
          <div
            className={`boolean-setting-col ${sequenceFieldWrapperClass('col-12 col-sm-6')}`.trim()}
        >
          <div
            ref={linkedParentToggleRef}
            className={showNestedPanel ? 'settings-child-parent-toggle w-100' : 'w-100'}
          >
            <div className='advanced-boolean-field'>
              {booleanLabel}
              {booleanSwitch}
            </div>
          </div>
          </div>
        ) : null}
        {sequencePresentationPart !== 'toggle' &&
        (showNestedPanel ||
          (sequencePresentationPart === 'panel' && nestedPanelWasVisibleRef.current)) ? (
          <div className='col-12'>
            <div ref={linkedChildPanelRef} className='settings-child-panel mb-1'>
              {hasFileActions ? (
                <div className='mb-4'>
                  <SettingFileActionCallout
                    mappings={fileActionMappings}
                    childSettings={fileActionChildren}
                    sourceChannelName={sourceChannelName}
                  />
                </div>
              ) : null}
              <ChannelSettingLinkedPanelLayoutProvider
                enabled={true}
              >
                <div className='row g-4'>
                {standardChildren.length
                  ? standardChildren.map(
                      (childSetting2: SETTINGS_OF_PLATFORM, childIndex2: number) => (
                        <DynamicChildSetting
                          key={id + childIndex2 + childItemIndex}
                          childItem={childSetting2}
                          childItemIndex={childIndex2}
                          settingField={settingField}
                          values={values}
                          handleChange={handleChange}
                          setFieldValue={setFieldValue}
                          settingName={settingName}
                          settingId={settingId}
                          savedMappings={savedMappings}
                          setFieldMappings={setFieldMappings}
                          fieldMappings={fieldMappings}
                          retrivedPlatformData={retrivedPlatformData}
                          mappingSourceSide={mappingSourceSide}
                          mappingDestinationSide={mappingDestinationSide}
                          mappingSourcePlatform={mappingSourcePlatform}
                          mappingDestinationPlatform={mappingDestinationPlatform}
                          usesSourceChildMappings={usesSourceChildMappings}
                          groupCode={groupCode}
                          secondaryOrderMappingsData={secondaryOrderMappingsData}
                          sourceStoreData={sourceStoreData}
                          setSavedMappings={setSavedMappings}
                          platformCode={platformCode}
                          sourceChannelName={sourceChannelName}
                        />
                      )
                    )
                  : null}
                {standardDefaultFieldMappings.length ? (
                  <div className='col-12'>
                    <div className='row g-4'>
                      {standardDefaultFieldMappings.map((field_mapping) => {
                        const savedMapping = findDefaultChildSavedMapping(
                          field_mapping,
                          savedMappings,
                          settingId
                        )
                        return (
                          <div
                            key={`${getSettingMappingId(field_mapping) ?? field_mapping.field_code}_${field_mapping.field_code}`}
                            className='col-12 col-lg-6'
                          >
                            <MappingInput
                              destinationPlatform={defaultMappingDestinationPlatformName}
                              sourcePlatform={sourcePlatformDisplayName}
                              selectedPlatformId={Number(selectedPlatformId)}
                              fullWidth
                              wrapperClassName='mb-0'
                              isMappingVisible={
                                values[`${settingField}_setting`][`${childItem.name}`]
                                  ? values[`${settingField}_setting`][`${childItem.name}`]
                                  : false
                              }
                              handleChange={handleChange}
                              field_mapping={field_mapping}
                              sourceSide={sourceSideId!}
                              destinationSide={defaultMappingDestinationSideId}
                              savedMapping={savedMapping}
                              settingName={settingName}
                              settingId={settingId}
                              setFieldMappings={setFieldMappings}
                              fieldMappings={fieldMappings}
                            />
                          </div>
                        )
                      })}
                      {usesSourceChildMappings &&
                        effectivePayoutCostsMapping &&
                        payoutDependentDestinationMappings.map((childMappingMeta, mappingIndex) => {
                          const fieldCode = childMappingMeta.field_code
                          if (!fieldCode) return null

                          const effectiveSettingMappingId =
                            getSettingMappingId(childMappingMeta as ChildFieldMappingMeta) ??
                            childMappingMeta.setting_mapping_id ??
                            null

                          const matchedSavedMapping = findDependentSavedFieldMapping(
                            payoutCostsApiCode,
                            fieldCode,
                            savedMappings,
                            settingId,
                            effectiveSettingMappingId
                          )

                          const resolvedDestinationField = mergePayoutPlatformFieldSide({
                            fetchedField: dependentDestinationFields[fieldCode],
                            savedField: matchedSavedMapping?.destination_field,
                            meta: childMappingMeta,
                            platformId: payoutDestinationSideId,
                          })
                          const resolvedSourceField = mergePayoutPlatformFieldSide({
                            fetchedField: dependentSourceFields[payoutCostsApiCode],
                            savedField: matchedSavedMapping?.source_field,
                            meta: effectivePayoutCostsMapping,
                            platformId: payoutSourceSideId,
                          })

                          if (!resolvedDestinationField?.id || !resolvedSourceField?.id) return null

                          const enrichedDestinationMapping = {
                            ...resolvedDestinationField,
                            api_code: fieldCode,
                            field_code: fieldCode,
                            display_name:
                              childMappingMeta.display_name || resolvedDestinationField.display_name,
                            platform: resolvedDestinationField.platform ?? payoutDestinationSideId,
                            isDefault: false,
                            isRequired: childMappingMeta.isRequired,
                            setting_mapping_id: effectiveSettingMappingId,
                          }
                          const enrichedSourceMapping = {
                            ...resolvedSourceField,
                            api_code: payoutCostsApiCode,
                            platform: resolvedSourceField.platform ?? payoutSourceSideId,
                            isDefault: false,
                            isRequired: childMappingMeta.isRequired,
                            setting_mapping_id: effectiveSettingMappingId,
                            map_with: enrichedDestinationMapping,
                          }

                          return (
                            <div
                              key={`dependent_${effectiveSettingMappingId ?? childMappingMeta.field_code}_${childMappingMeta.field_code}`}
                              className='col-12'
                            >
                              <DependentMapping
                                selectedPlatformId={Number(payoutSourceSideId ?? selectedPlatformId)}
                                sourcePlatform={
                                  sourceStoreData?.platform_info?.display_name ??
                                  sourceStoreData?.display_name
                                }
                                destinationPlatform={
                                  retrivedPlatformData?.platform_info?.display_name ??
                                  retrivedPlatformData?.display_name
                                }
                                isMappingVisible={isEnabled}
                                handleChange={handleChange}
                                field_mapping={enrichedSourceMapping}
                                isMappedWith={enrichedDestinationMapping}
                                savedMapping={matchedSavedMapping}
                                settingName={settingName}
                                settingId={settingId}
                                setFieldMappings={setFieldMappings}
                                fieldMappings={fieldMappings}
                                index={mappingIndex}
                                savedMappings={savedMappings}
                                setSavedMappings={setSavedMappings!}
                                primary_settings={true}
                                group_code={groupCode ?? 'ERP_ORDER'}
                                platformCode={(platformCode ?? '').toUpperCase()}
                                useGroup2ChildMappingDirection={useGroup2ChildMappingDirection}
                              />
                            </div>
                          )
                        })}
                    </div>
                  </div>
                ) : null}
                {hasNonDefaultMappings ? (
                  <div className='col-12'>
                    <div className='row g-4'>{renderNonDefaultFieldMappings()}</div>
                  </div>
                ) : null}
                </div>
              </ChannelSettingLinkedPanelLayoutProvider>
            </div>
          </div>
        ) : null}
      </>
    )
  } else if (childItem.input_type === 'INTEGER') {
    return (
      <CustomInput
        label={formatSettingDisplayText(childItem.display_name)}
        message={childItem.description}
        sourceChannelName={sourceChannelName}
        containerClasses={getSequenceCustomInputContainerClass(undefined, {insideLinkedPanel})}
        useSequenceBandLayout={true}
        inputProps={{
          type: 'number',
          className: 'form-control form-control-lg',
          name: settingField ? `${settingField}_setting.${childItem.name}` : childItem.name,
          placeholder: formatSettingDisplayText(childItem?.placeholder ?? ''),
          value: settingField
            ? values[`${settingField}_setting`][`${childItem.name}`]
            : values[childItem.name],
          onChange: handleChange,
          id: childItem.name + childItemIndex,
          required: childItem.is_required || false,
        }}
      />
    )
  } else if (childItem.input_type === 'DATETIME') {
    // If sync_start_date exist, disable date-time editing
    if (retrivedPlatformData?.platform_settings?.order_setting?.sync_start_date && !syncStartDate) {
      setSyncStartDate(true)
    }
    const formattedLocalDateString = formatDateString(
      values[`${settingField}_setting`][`${childItem.name}`]
    )

    return (
      <CustomInput
        label={formatSettingDisplayText(childItem.display_name)}
        message={childItem.description}
        sourceChannelName={sourceChannelName}
        containerClasses={getSequenceCustomInputContainerClass(undefined, {insideLinkedPanel})}
        useSequenceBandLayout={true}
        inputProps={{
          type: 'datetime-local',
          className: 'form-control form-control-lg',
          name: settingField ? `${settingField}_setting.${childItem.name}` : childItem.name,
          placeholder: formatSettingDisplayText(childItem?.placeholder ?? ''),
          value: formattedLocalDateString ? formattedLocalDateString : '',
          // value: settingField
          //   ? `${new Date(values[`${settingField}_setting`][`${childItem.name}`])
          //       .toISOString()
          //       .slice(0, 16)}`
          //   : '',
          onChange: handleChange,
          id: childItem.name + childItemIndex,
          required: childItem.is_required || false,
          disabled: syncStartDate,
        }}
      />
    )
  } else if (childItem.input_type === 'SELECT') {
    const selectLabel = (
      <label className='form-label' htmlFor={childItem.name}>
        {formatSettingDisplayText(childItem.display_name)}
        &nbsp;&nbsp;
        <InfoTooltip message={childItem.description}
        sourceChannelName={sourceChannelName} />
      </label>
    )
    const selectControl = (
      <select
        name={settingField ? `${settingField}_setting.${childItem.name}` : childItem.name}
        value={
          settingField
            ? values[`${settingField}_setting`][`${childItem.name}`]
            : values[childItem.name]
        }
        onChange={handleChange}
        className='form-select form-select-lg'
        required={childItem.is_required || false}
      >
        <option value=''>{formatSettingDisplayText(childItem.placeholder)}</option>
        {childItem.setting_options.map((item: any) => {
          return (
            <option key={item.id} value={item.name}>
              {formatSettingDisplayText(item.display_name)}
            </option>
          )
        })}
      </select>
    )

    return (
      <div
        className={sequenceFieldWrapperClass('col-12 col-sm-6 mt-4')}
        style={getSequenceFieldShellStyle()}
      >
        <SequenceFieldShell label={selectLabel} control={selectControl} />
      </div>
    )
  } else if (childItem.input_type === 'MULTISELECT') {
    const options = childItem.setting_options.map((item: any) => ({
      value: item.name,
      label: formatSettingDisplayText(item.display_name),
    }))

    // For now getting value as comma separated strings, later change to array of strings
    const selectedValueString = settingField
      ? String(values[`${settingField}_setting`]?.[childItem.name] || '')
      : String(values[childItem.name] || '')

    const selectedValue = selectedValueString
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean) // Filter out any empty values

    const handleSelectChange = (selectedOptions: any) => {
      // Convert the selected options array into a comma-separated string
      const newValue = selectedOptions
        ? selectedOptions.map((option: any) => option.value).join(', ')
        : ''

      const selectEvent = {
        target: {
          name: settingField ? `${settingField}_setting.${childItem.name}` : childItem.name,
          value: newValue,
        },
      }

      handleChange(selectEvent as React.ChangeEvent<any>)
    }
    const multiselectLabel = (
      <label className='form-label' htmlFor={childItem.name}>
        {formatSettingDisplayText(childItem.display_name)}
        &nbsp;&nbsp;
        <InfoTooltip message={childItem.description}
        sourceChannelName={sourceChannelName} />
      </label>
    )
    const multiselectControl = (
      <Select
        className='react-select'
        classNamePrefix='select'
        name={settingField ? `${settingField}_setting.${childItem.name}` : childItem.name}
        value={options.filter((option) => selectedValue.includes(option.value))}
        onChange={handleSelectChange}
        options={options}
        isMulti
        placeholder={formatSettingDisplayText(childItem.placeholder)}
        required={childItem.is_required || false}
      />
    )

    return (
      <div
        className={sequenceFieldWrapperClass('col-12 col-sm-6 mt-4')}
        style={getSequenceFieldShellStyle()}
      >
        <SequenceFieldShell label={multiselectLabel} control={multiselectControl} />
      </div>
    )
  } else {
    //default is text field
    if (childItem.showShortcodes) {
      return (
        <>
          <ShortCodeInput
            label={formatSettingDisplayText(childItem.display_name)}
            message={childItem.description}
        sourceChannelName={sourceChannelName}
            containerClasses={getSequenceCustomInputContainerClass(undefined, {insideLinkedPanel})}
            useSequenceBandLayout={true}
            inputProps={{
              type: 'text',
              className: 'form-control form-control-lg',
              name: settingField ? `${settingField}_setting.${childItem.name}` : childItem.name,
              placeholder: formatSettingDisplayText(childItem?.placeholder ?? ''),
              value: settingField
                ? values[`${settingField}_setting`][`${childItem.name}`]
                : values[childItem.name],
              onChange: handleChange,
              id: childItem.name + childItemIndex,
              required: childItem?.is_required || false,
            }}
          />
        </>
      )
    } else {
      return (
        <CustomInput
          label={formatSettingDisplayText(childItem.display_name)}
          message={childItem.description}
        sourceChannelName={sourceChannelName}
          containerClasses={getSequenceCustomInputContainerClass(undefined, {insideLinkedPanel})}
          useSequenceBandLayout={true}
          inputProps={{
            type: 'text',
            className: 'form-control form-control-lg',
            name: settingField ? `${settingField}_setting.${childItem.name}` : childItem.name,
            placeholder: formatSettingDisplayText(childItem?.placeholder ?? ''),
            value: settingField
              ? values[`${settingField}_setting`][`${childItem.name}`]
              : values[childItem.name],
            onChange: handleChange,
            id: childItem.name + childItemIndex,
            required: childItem?.is_required || false,
          }}
        />
      )
    }
  }

  if (isSettingFileChildInputType(childItem.input_type)) {
    return null
  }

  return null
}

export default DynamicChildSetting
